import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IMAGE_OVERLAY } from "shared/context/overlay/types"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { TabType } from "shared/types/Tab"
import { PropertyList } from "shared/ui/property-list/PropertyList"

import { ProductExternalDimensioning } from "entities/products/ui/product-external-dimensioning/ProductExternalDimensioning"
import { VariantKeyDisplayProps } from "entities/products/ui/variant-key-display/VariantKeyDisplay"

import { ProductConfiguratorSummaryCardProps } from "../ProductConfiguratorSummaryCard"
import { ProductConfiguratorSummaryCardAddons } from "../ProductConfiguratorSummaryCardAddons"

export enum ProductConfiguratorSummaryCardTab {
  DIMENSIONING = "dimensioning",
  CONFIGURATION = "configuration",
  ADDONS = "addons"
}

export function useProductConfiguratorSummaryCardViewModel({
  className,
  productImage,
  productTitle,
  productInformation,
  isLoadingProductInformation,
  showVariantKeysOnly
}: ProductConfiguratorSummaryCardProps) {
  const { t } = useTranslation()

  const { openOverlay } = useOverlay(IMAGE_OVERLAY)

  const [selectedTab, setSelectedTab] = useState<string | null>(null)

  const isUpdating = Boolean(isLoadingProductInformation && productInformation)
  const isLoading = Boolean(isLoadingProductInformation && !productInformation)

  const tabs = useMemo<TabType[]>(() => {
    if (showVariantKeysOnly) {
      return []
    }

    const result: TabType[] = []

    if (productInformation?.dimensioning) {
      result.push({
        key: ProductConfiguratorSummaryCardTab.DIMENSIONING,
        title: t("Product.Configuration.Dimensioning"),
        content: (
          <ProductExternalDimensioning
            productInformation={productInformation}
            isLoading={isLoading}
          />
        )
      })
    }

    if (productInformation?.attributes?.length) {
      result.push({
        key: ProductConfiguratorSummaryCardTab.CONFIGURATION,
        title: t("Product.Configuration.Configuration"),
        content: (
          <PropertyList
            list={productInformation.attributes}
            isLoading={isLoading}
          />
        )
      })
    }

    if (productInformation?.addons?.length) {
      result.push({
        key: ProductConfiguratorSummaryCardTab.ADDONS,
        title: t("Product.Configuration.Addon.Addons"),
        content: (
          <ProductConfiguratorSummaryCardAddons
            addons={productInformation.addons}
            isLoading={isLoading}
          />
        )
      })
    }

    return result
  }, [t, showVariantKeysOnly, productInformation, isLoading])

  const activeTab = useMemo<string | null>(() => {
    if (tabs.some(tab => tab.key === selectedTab)) {
      return selectedTab
    }
    return tabs[0]?.key ?? null
  }, [tabs, selectedTab])

  const handleTabChange = useEvent((key: string | null) => {
    if (key) {
      setSelectedTab(key)
    }
  })

  const handleZoomImage = useEvent(() => {
    if (!productImage) return

    openOverlay({
      content: productImage,
      title: productInformation?.shortTitle || productTitle || undefined
    })
  })

  const titleByProductType = useMemo(
    () =>
      Object.fromEntries(
        (productInformation?.addons ?? []).map(addon => [
          addon.productType,
          addon.title
        ])
      ),
    [productInformation?.addons]
  )

  const variantKeyDisplayProps = useMemo<VariantKeyDisplayProps>(
    () => ({
      title: productInformation?.title,
      variantKey: productInformation?.variantKey,
      addons: productInformation?.addons,
      titleByProductType
    }),
    [productInformation, titleByProductType]
  )

  return {
    className: classNames(
      "product-configurator-summary-card card card--elevated",
      className
    ),
    image: productImage,
    onZoomImage: productImage ? handleZoomImage : undefined,
    shortTitle: productInformation?.shortTitle || productTitle,
    kind: productInformation?.productKind,
    title: productInformation?.title,
    isLoading,
    isUpdating,

    showVariantKeysOnly,
    variantKeyDisplayProps,

    tabs,
    activeTab,
    onTabChange: handleTabChange
  }
}
